import { AdditionalChainLinks, useChainLengthCalculator } from "src/components/Calculator/useChainLengthCalculator";
import { Box, Card, MenuItem, Select } from "@material-ui/core";
import {
  CalculatorBodyText,
  CalculatorCaption,
  CalculatorElevatedText,
  CalculatorHeadline,
  CalculatorProminentText,
  CalculatorTextCenter,
  CalculatorTextHighlight,
  CalculatorTopicHeadline,
  PreCalculatorHeadline,
} from "src/components/Calculator/CalculatorTypography";
import React, { ChangeEvent, FC } from "react";

import { CalculatorImage } from "src/components/Calculator/CalculatorImage";
import { CalculatorLayout } from "src/components/Calculator/CalculatorLayout";
import CycleverseDivider from "src/components/CycleverseDivider";
import CycleverseSelect from "src/components/CycleverseSelect";
import { CycleverseSlider } from "src/components/CycleverseSlider";
import LocaleLink from "src/components/Navigation/LocaleLink";
import SeoHead from "src/components/Seo/SeoHead";
import useTranslation from "next-translate/useTranslation";

const BikeChainLenghtCalculator: FC = () => {
  const { t } = useTranslation("chainLength");
  const {
    MinKs,
    MaxKs,
    ks,
    setKs,
    MinZk,
    MaxZk,
    zk,
    setZk,
    MinZr,
    MaxZr,
    zr,
    setZr,
    z,
    setZ,
    kl,
    l,
  } = useChainLengthCalculator();

  return (
    <CalculatorLayout
      header={
        <div data-test="page-rechner-kettenlaenge-berechnen">
          <PreCalculatorHeadline>{t("calculatorChainLengthPreHeadline")}</PreCalculatorHeadline>
          <CalculatorHeadline>{t("calculatorChainLengthHeadline")}</CalculatorHeadline>
        </div>
      }
    >
      <SeoHead title={t("calculatorChainLengthSeoTitle")} description={t("calculatorChainLengthSeoDescription")} />
      <CalculatorImage
        // TODO: FIXME: link should show our images.thecycleverse
        src="/images/calculators/kettenlaenge-main.png"
        width={600}
        height={363}
        layout="intrinsic"
        alt="Kettenlänge berechnen"
      />
      <CalculatorBodyText>{t("calculatorChainLengthText00")}</CalculatorBodyText>
      <CalculatorBodyText>{t("calculatorChainLengthText01")}</CalculatorBodyText>
      <CalculatorTopicHeadline>{t("calculatorChainLengthText02")}</CalculatorTopicHeadline>
      <CalculatorBodyText>{t("calculatorChainLengthText03")}</CalculatorBodyText>
      <CalculatorElevatedText>
        <CalculatorTextCenter>
          <CalculatorTextHighlight>{ks}cm</CalculatorTextHighlight>
        </CalculatorTextCenter>
      </CalculatorElevatedText>

      <CycleverseSlider
        value={ks}
        min={MinKs}
        max={MaxKs}
        step={1}
        marks={[
          { value: MinKs, label: MinKs },
          { value: MaxKs, label: MaxKs },
        ]}
        onChange={(event: ChangeEvent<{}>, value: number): void => setKs(Number(value))}
        valueLabelDisplay="auto"
      />
      <CalculatorTopicHeadline>{t("calculatorChainLengthText04")}</CalculatorTopicHeadline>
      <CalculatorElevatedText>
        <CalculatorTextCenter>
          <CalculatorTextHighlight>
            {zk} {t("calculatorChainLengthText05")}
          </CalculatorTextHighlight>
        </CalculatorTextCenter>
      </CalculatorElevatedText>
      <CycleverseSlider
        value={zk}
        min={MinZk}
        max={MaxZk}
        step={1}
        marks={[
          { value: MinZk, label: MinZk },
          { value: MaxZk, label: MaxZk },
        ]}
        onChange={(_, value) => setZk(Number(value))}
        valueLabelDisplay="auto"
      />
      <CalculatorTopicHeadline>{t("calculatorChainLengthText06")}</CalculatorTopicHeadline>
      <CalculatorElevatedText>
        <CalculatorTextCenter>
          <CalculatorTextHighlight>
            {zr} {t("calculatorChainLengthText07")}
          </CalculatorTextHighlight>
        </CalculatorTextCenter>
      </CalculatorElevatedText>
      <CycleverseSlider
        value={zr}
        min={MinZr}
        max={MaxZr}
        step={1}
        marks={[
          { value: MinZr, label: MinZr },
          { value: MaxZr, label: MaxZr },
        ]}
        onChange={(_, value) => setZr(Number(value))}
        valueLabelDisplay="auto"
      />
      <CalculatorTopicHeadline>{t("calculatorChainLengthText08")}</CalculatorTopicHeadline>
      <CalculatorElevatedText>
        <CalculatorTextCenter>
          <CalculatorTextHighlight>
            {z ? `${z} ${t("calculatorChainLengthText09")}` : t("calculatorChainLengthText09_none")}
          </CalculatorTextHighlight>
        </CalculatorTextCenter>
      </CalculatorElevatedText>
      <Box display="flex" justifyContent="center">
        {/* TODO: kjellskis - style of select for theme */}
        <CycleverseSelect
          value={"2"}
          onChange={(e) => {
            setZ(Number(e.target.value));
          }}
        >
          {Object.keys(["1", "2"])
            .filter((key) => isNaN(Number(key)))
            .map((key) => (
              <MenuItem key={key} value={AdditionalChainLinks[key]}>
                {t(`calculatorChainLengthExtraTeeth${key}`)}
              </MenuItem>
            ))}
        </CycleverseSelect>
      </Box>
      <Box marginTop={8} />
      <CalculatorTopicHeadline>{t("calculatorChainLengthText10")}</CalculatorTopicHeadline>
      <Box display="flex" justifyContent="center" alignItems="center" textAlign="center" marginTop={2}>
        <Card>
          <Box padding={4}>
            <CalculatorProminentText>
              <CalculatorTextHighlight>
                {kl} {t("calculatorChainLengthText11")}
              </CalculatorTextHighlight>
              <br />
              {t("calculatorChainLengthText12")}
              <br />
              <CalculatorTextHighlight>
                {l} {t("calculatorChainLengthText13")}
              </CalculatorTextHighlight>
            </CalculatorProminentText>
          </Box>
        </Card>
      </Box>
      <CycleverseDivider marginTop={4} />
      <CalculatorCaption>{t("calculatorChainLengthText14")}</CalculatorCaption>
      <CalculatorBodyText>{t("calculatorChainLengthText15")}</CalculatorBodyText>
      <CalculatorBodyText>
        {t("calculatorChainLengthText16")}
        <LocaleLink href={`/blog/[generic]`} as={`/blog/fahrradkette-kuerzen-anleitung`} target="new">
          {t("calculatorChainLengthText17")}
        </LocaleLink>
        ).
      </CalculatorBodyText>
      <CalculatorCaption>{t("calculatorChainLengthText18")}</CalculatorCaption>
      <CalculatorBodyText>{t("calculatorChainLengthText19")}</CalculatorBodyText>
      <ul>
        <li>
          <CalculatorBodyText>{t("calculatorChainLengthText20")}</CalculatorBodyText>
        </li>

        <li>
          <CalculatorBodyText>{t("calculatorChainLengthText21")}</CalculatorBodyText>
        </li>

        <li>
          <CalculatorBodyText>{t("calculatorChainLengthText22")}</CalculatorBodyText>
        </li>

        <li>
          <CalculatorBodyText>{t("calculatorChainLengthText23")}</CalculatorBodyText>
        </li>
      </ul>
      <CalculatorBodyText>{t("calculatorChainLengthText24")}</CalculatorBodyText>
      <Box textAlign="center" marginTop={2}>
        <CalculatorBodyText>
          {t("calculatorChainLengthText25")} (KL) = 0,157 * KS + ZK / 2 + ZR / 2 + Z
        </CalculatorBodyText>
        <i>{t("calculatorChainLengthText26")}</i>
      </Box>
      <CalculatorBodyText>{t("calculatorChainLengthText27")}</CalculatorBodyText>
      <CalculatorBodyText>{t("calculatorChainLengthText28")}</CalculatorBodyText>
      <ul>
        <li>
          <CalculatorBodyText>{t("calculatorChainLengthText29")}</CalculatorBodyText>
        </li>
        <li>
          <CalculatorBodyText>{t("calculatorChainLengthText30")}</CalculatorBodyText>
        </li>
        <li>
          <CalculatorBodyText>{t("calculatorChainLengthText31")}</CalculatorBodyText>
        </li>
        <li>
          <CalculatorBodyText>{t("calculatorChainLengthText32")}</CalculatorBodyText>
        </li>
      </ul>
      <CalculatorBodyText>{t("calculatorChainLengthText33")}</CalculatorBodyText>
      <ul>
        <li>
          <CalculatorBodyText>{t("calculatorChainLengthText34")}</CalculatorBodyText>
        </li>
        <li>
          <CalculatorBodyText>{t("calculatorChainLengthText35")}</CalculatorBodyText>
        </li>
        <li>
          <CalculatorBodyText>{t("calculatorChainLengthText36")}</CalculatorBodyText>
        </li>
        <li>
          <CalculatorBodyText>{t("calculatorChainLengthText37")}</CalculatorBodyText>
        </li>
        <li>
          <CalculatorBodyText>{t("calculatorChainLengthText38")}</CalculatorBodyText>
        </li>
      </ul>
      <CalculatorBodyText>{t("calculatorChainLengthText39")}</CalculatorBodyText>
      <CalculatorBodyText>{t("calculatorChainLengthText40")}</CalculatorBodyText>
      <CalculatorBodyText>{t("calculatorChainLengthText41")}</CalculatorBodyText>
      <CalculatorCaption>{t("calculatorChainLengthText42")}</CalculatorCaption>
      <CalculatorBodyText>{t("calculatorChainLengthText43")}</CalculatorBodyText>
      <CalculatorBodyText>{t("calculatorChainLengthText44")}</CalculatorBodyText>
      <CalculatorBodyText>{t("calculatorChainLengthText45")}</CalculatorBodyText>
      <CalculatorBodyText>{t("calculatorChainLengthText46")}</CalculatorBodyText>
      <CalculatorCaption>{t("calculatorChainLengthText47")}</CalculatorCaption>
      <CalculatorBodyText>{t("calculatorChainLengthText48")}</CalculatorBodyText>
      <CalculatorBodyText>
        {t("calculatorChainLengthText49")}
        <LocaleLink href={`/blog/[generic]`} as={`/singlespeed-fixie`} target="new">
          {t("calculatorChainLengthText50")}
        </LocaleLink>{" "}
        {t("calculatorChainLengthText51")}
      </CalculatorBodyText>
      <CalculatorBodyText>{t("calculatorChainLengthText52")}</CalculatorBodyText>
      <CalculatorBodyText>{t("calculatorChainLengthText53")}</CalculatorBodyText>
      <CalculatorBodyText>{t("calculatorChainLengthText54")}</CalculatorBodyText>
      <CalculatorBodyText>{t("calculatorChainLengthText55")}</CalculatorBodyText>
      <CalculatorCaption>{t("calculatorChainLengthText56")}</CalculatorCaption>
      <CalculatorBodyText>{t("calculatorChainLengthText57")}</CalculatorBodyText>
      <CalculatorBodyText>{t("calculatorChainLengthText58")}</CalculatorBodyText>
      <CalculatorBodyText>{t("calculatorChainLengthText59")}</CalculatorBodyText>
    </CalculatorLayout>
  );
};

export default BikeChainLenghtCalculator;
